import { useState, lazy, Suspense, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { apiConfig } from '../lib/apiConfig';
import { getArticlesArray } from '../store';
import GraphView from './GraphView';
import UniverseState from './UniverseState';
import { trackEvent, setOnceUserProperties } from '../lib/analytics';
import './UniverseView.css';
import { ICON_SIZE } from '../design-system';

const MapView = lazy(() => import('./MapView'));
const TopicsView = lazy(() => import('./TopicsView'));

const CACHE_KEY_PREFIX = 'reedle_universe_v2_';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MIN_ARTICLES = 5;
const getUniverseErrorKind = (message) => (
  /not enough|no embeddings|still being indexed/i.test(String(message)) ? 'empty' : 'error'
);

const UniverseView = ({ userId, listId = 'all' }) => {
  const { t } = useTranslation('reader');
  const [subTab, setSubTab] = useState('graph');
  const [universeData, setUniverseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const workerRef = useRef(null);

  // getArticlesArray expects null for inbox, 'all' for all, or UUID for a list
  const storeListId = listId === 'inbox' ? null : listId;

  const articles = useSelector(() =>
    getArticlesArray({ listId: storeListId, archivedOnly: false, sortBy: 'newest' })
  );
  const archivedArticles = useSelector(() =>
    getArticlesArray({ listId: storeListId, archivedOnly: true, sortBy: 'newest' })
  );

  // Merge active + archived for universe (archived = read & filed away = still part of your universe)
  const allArticles = useMemo(() => [...articles, ...archivedArticles], [articles, archivedArticles]);
  const archivedIds = useMemo(() => new Set(archivedArticles.map((a) => a.id)), [archivedArticles]);

  const articleMap = useMemo(() => {
    const map = {};
    allArticles.forEach((a) => { map[a.id] = a; });
    return map;
  }, [allArticles]);

  // Refresh: clear all cache entries for this context and recompute
  const handleRefresh = useCallback(() => {
    const prefix = `${CACHE_KEY_PREFIX}${userId}_${listId}_`;
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith(prefix))
        .forEach((k) => localStorage.removeItem(k));
    } catch (error) {
      console.warn('[UniverseView] Could not clear cached results before recompute.', error);
    }
    trackEvent('universe_refresh_clicked', { list_id: listId });
    setRefreshKey((k) => k + 1);
  }, [userId, listId]);

  // Retry the request without touching cached results. Cache invalidation is
  // reserved for the explicit recompute action above.
  const handleRetry = useCallback(() => {
    trackEvent('universe_retry_clicked', { list_id: listId });
    setRefreshKey((k) => k + 1);
  }, [listId]);

  // Fetch AI cluster labels for an assignment map
  async function fetchClusterLabels(assignMap, nodeIds, aMap) {
    try {
      const clusterTitles = {};
      nodeIds.forEach((id) => {
        const c = assignMap[id];
        if (c === undefined || c === null || c < 0) return;
        if (!clusterTitles[c]) clusterTitles[c] = [];
        const title = aMap[id]?.title;
        if (title) clusterTitles[c].push(title);
      });

      const clusters = Object.entries(clusterTitles)
        .filter(([, titles]) => titles.length >= 3)
        .map(([id, titles]) => ({ id: Number(id), titles: titles.slice(0, 5) }));

      if (!clusters.length) return {};

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return {};

      const res = await fetch(`${apiConfig.supabaseUrl}/functions/v1/reedle-cluster-labels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ clusters }),
      });

      if (!res.ok) {
        console.warn('[UniverseView] Cluster label request failed.', { status: res.status });
        return {};
      }
      const { labels } = await res.json();
      return labels || {};
    } catch (error) {
      console.warn('[UniverseView] Cluster labels unavailable.', error);
      return {};
    }
  }

  // Build universeData from worker output + nodeIds
  async function buildUniverseData(msg, nodeIds, aMap, isActive = () => true) {
    const { coords, kmeansAssign, kmK, dbscanAssign, hierL1, hierL2, hierL2ToL1, hierL1k, hierL2k } = msg;

    const kmeansMap = {};
    const dbscanMap = {};
    const hierL1Map = {};
    const hierL2Map = {};
    nodeIds.forEach((id, i) => {
      kmeansMap[id] = kmeansAssign[i];
      dbscanMap[id] = dbscanAssign ? dbscanAssign[i] : kmeansAssign[i];
      hierL1Map[id] = hierL1[i];
      hierL2Map[id] = hierL2[i];
    });

    const dbscanValidIds = dbscanAssign
      ? nodeIds.filter(id => dbscanMap[id] >= 0)
      : nodeIds;

    const [kmeansLabels, dbscanLabels, hierLabelsPair] = await Promise.all([
      fetchClusterLabels(kmeansMap, nodeIds, aMap),
      fetchClusterLabels(dbscanMap, dbscanValidIds, aMap),
      fetchClusterLabels(hierL1Map, nodeIds, aMap)
        .then(async l1Labels => {
          const l2Labels = await fetchClusterLabels(hierL2Map, nodeIds, aMap);
          return { l1: l1Labels, l2: l2Labels };
        }),
    ]);

    if (!isActive()) return;

    setUniverseData({
      coords,
      nodeIds,
      articleMap: aMap,
      kmeansAssign: kmeansMap,
      kmK,
      kmeansLabels,
      dbscanAssign: dbscanMap,
      dbscanLabels,
      hierL1: hierL1Map,
      hierL2: hierL2Map,
      hierL2ToL1,
      hierL1k,
      hierL2k,
      hierL1Labels: hierLabelsPair.l1,
      hierL2Labels: hierLabelsPair.l2,
    });
  }

  // Restore from cache: render immediately, then fetch labels async
  async function restoreFromCache(parsed, aMap, isActive = () => true) {
    const { coords, kmeansAssign, kmK, dbscanAssign, hierL1, hierL2, hierL2ToL1, hierL1k, hierL2k } = parsed;
    const nodeIds = coords.map(c => c.id);

    const kmeansMap = {};
    const dbscanMap = {};
    const hierL1Map = {};
    const hierL2Map = {};
    nodeIds.forEach((id, i) => {
      kmeansMap[id] = kmeansAssign ? kmeansAssign[i] : 0;
      dbscanMap[id] = dbscanAssign ? dbscanAssign[i] : (kmeansAssign ? kmeansAssign[i] : 0);
      hierL1Map[id] = hierL1 ? hierL1[i] : 0;
      hierL2Map[id] = hierL2 ? hierL2[i] : 0;
    });

    // Render immediately without labels
    if (!isActive()) return;

    setUniverseData({
      coords,
      nodeIds,
      articleMap: aMap,
      kmeansAssign: kmeansMap,
      kmK: kmK || 6,
      kmeansLabels: {},
      dbscanAssign: dbscanMap,
      dbscanLabels: {},
      hierL1: hierL1Map,
      hierL2: hierL2Map,
      hierL2ToL1: hierL2ToL1 || {},
      hierL1k: hierL1k || 3,
      hierL2k: hierL2k || 6,
      hierL1Labels: {},
      hierL2Labels: {},
    });

    // Fetch labels async and update
    const dbscanValidIds = dbscanAssign
      ? nodeIds.filter(id => dbscanMap[id] >= 0)
      : nodeIds;

    const [kmeansLabels, dbscanLabels, hierLabelsPair] = await Promise.all([
      fetchClusterLabels(kmeansMap, nodeIds, aMap),
      fetchClusterLabels(dbscanMap, dbscanValidIds, aMap),
      fetchClusterLabels(hierL1Map, nodeIds, aMap)
        .then(async l1Labels => {
          const l2Labels = await fetchClusterLabels(hierL2Map, nodeIds, aMap);
          return { l1: l1Labels, l2: l2Labels };
        }),
    ]);

    if (!isActive()) return;

    setUniverseData(prev => prev ? {
      ...prev,
      kmeansLabels,
      dbscanLabels,
      hierL1Labels: hierLabelsPair.l1,
      hierL2Labels: hierLabelsPair.l2,
    } : prev);
  }

  // Main pipeline: fetch embeddings → filter by list → worker → cluster → labels
  useEffect(() => {
    if (!userId || allArticles.length < MIN_ARTICLES) return;

    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
      workerRef.current?.terminate();
      workerRef.current = null;
    };
    const reportPipelineError = (rawError, eventName = 'universe_error') => {
      if (cancelled) return;
      const message = rawError?.message || String(rawError || 'Unknown universe error');
      console.error(`[UniverseView] ${eventName}`, rawError);
      trackEvent(eventName, { error_message: message, list_id: listId });
      setError({ kind: getUniverseErrorKind(message), message });
      setLoading(false);
    };

    trackEvent('universe_viewed', { article_count: allArticles.length, list_id: listId });
    setOnceUserProperties({ first_universe_viewed_at: new Date().toISOString() });
    trackEvent('first_universe_viewed', { article_count: allArticles.length });

    // Reset stale data immediately when list context or refreshKey changes
    setUniverseData(null);
    setError(null);
    setLoading(false);

    const cacheKey = `${CACHE_KEY_PREFIX}${userId}_${listId}_${allArticles.length}`;

    // Try cache first (skip if TTL expired)
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const age = Date.now() - (parsed.cachedAt || 0);
        if (
          age < CACHE_TTL_MS &&
          Math.abs(parsed.articleCount - allArticles.length) <= 5 &&
          parsed.coords?.length
        ) {
          trackEvent('universe_loaded', { node_count: parsed.coords.length, list_id: listId, from_cache: true });
          restoreFromCache(parsed, articleMap, () => !cancelled).catch((error) => {
            reportPipelineError(error, 'universe_cache_restore_error');
          });
          return cleanup;
        }
      }
    } catch (error) {
      console.warn('[UniverseView] Ignoring invalid cached results.', error);
    }

    // Fresh compute
    setLoading(true);
    setProgressStep(0);

    supabase
      .rpc('reedle_get_article_embeddings', { p_user_id: userId, p_limit: 300 })
      .then(async ({ data, error: rpcErr }) => {
        if (cancelled) return;
        if (rpcErr) {
          reportPipelineError(rpcErr);
          return;
        }

        // Filter embeddings to only articles in the current list context
        const filteredData = (data || []).filter(d => articleMap[d.article_id]);
        if (!filteredData.length) {
          if (cancelled) return;
          setError({ kind: 'empty' });
          setLoading(false);
          return;
        }

        setProgressStep(1);

        const worker = new Worker(
          new URL('../workers/umap.worker.js', import.meta.url),
          { type: 'module' },
        );
        workerRef.current = worker;

        worker.onmessage = async (e) => {
          if (cancelled) {
            worker.terminate();
            return;
          }
          const msg = e.data;
          if (msg.progress === 'pca') setProgressStep(1);
          if (msg.progress === 'umap') setProgressStep(2);
          if (msg.progress === 'clustering') setProgressStep(3);
          if (msg.error) {
            reportPipelineError(msg.error);
            worker.terminate();
            return;
          }
          if (msg.done) {
            const nodeIds = filteredData.map(d => d.article_id);
            trackEvent('universe_loaded', { node_count: nodeIds.length, article_count: allArticles.length, list_id: listId, from_cache: false });
            // Cache raw results (no labels) with timestamp for TTL
            try {
              localStorage.setItem(cacheKey, JSON.stringify({
                coords: msg.coords,
                kmeansAssign: msg.kmeansAssign,
                kmK: msg.kmK,
                dbscanAssign: msg.dbscanAssign,
                hierL1: msg.hierL1,
                hierL2: msg.hierL2,
                hierL2ToL1: msg.hierL2ToL1,
                hierL1k: msg.hierL1k,
                hierL2k: msg.hierL2k,
                articleCount: filteredData.length,
                cachedAt: Date.now(),
              }));
            } catch (error) {
              console.warn('[UniverseView] Could not cache computed universe results.', error);
            }

            try {
              await buildUniverseData(msg, nodeIds, articleMap, () => !cancelled);
              if (!cancelled) setLoading(false);
            } catch (error) {
              reportPipelineError(error);
            } finally {
              worker.terminate();
              if (workerRef.current === worker) workerRef.current = null;
            }
          }
        };

        worker.onerror = (event) => {
          reportPipelineError(event.error || event.message, 'universe_worker_error');
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;
        };

        worker.postMessage({
          embeddings: filteredData.map((d) => d.embedding),
          ids: filteredData.map((d) => d.article_id),
        });
      })
      .catch((error) => {
        reportPipelineError(error, 'universe_embeddings_error');
      });

    return cleanup;
  }, [userId, listId, allArticles.length, refreshKey]);

  if (allArticles.length < MIN_ARTICLES) {
    const needed = MIN_ARTICLES - allArticles.length;
    return (
      <div className="universe-empty">
        <UniverseState
          kind="empty"
          title={t('universe.empty.articleTitle')}
          description={t('universe.empty.articleBody', { count: needed })}
        />
      </div>
    );
  }

  return (
    <div className="universe-view">
      <div className="universe-subtabs">
        <button
          className={`universe-subtab ${subTab === 'graph' ? 'active' : ''}`}
          aria-pressed={subTab === 'graph'}
          onClick={() => { setSubTab('graph'); trackEvent('universe_tab_changed', { tab: 'graph', list_id: listId }); }}
        >
          {t('universe.tabs.graph')}
        </button>
        <button
          className={`universe-subtab ${subTab === 'map' ? 'active' : ''}`}
          aria-pressed={subTab === 'map'}
          onClick={() => { setSubTab('map'); trackEvent('universe_tab_changed', { tab: 'map', list_id: listId }); }}
        >
          {t('universe.tabs.map')}
        </button>
        <button
          className={`universe-subtab ${subTab === 'topics' ? 'active' : ''}`}
          aria-pressed={subTab === 'topics'}
          onClick={() => { setSubTab('topics'); trackEvent('universe_tab_changed', { tab: 'topics', list_id: listId }); }}
        >
          {t('universe.tabs.topics')}
        </button>
        <button
          className="universe-refresh-btn"
          onClick={handleRefresh}
          title={t('universe.recompute')}
          aria-label={t('universe.recompute')}
          disabled={loading}
        >
          <RotateCcw size={ICON_SIZE.xs} />
        </button>
      </div>

      <div className="universe-canvas">
        {loading && (
          <UniverseState
            kind="loading"
            title={[
              t('universe.loading.steps.fetchingEmbeddings'),
              t('universe.loading.steps.pca'),
              t('universe.loading.steps.umap'),
              t('universe.loading.steps.clustering'),
            ][progressStep] || t('universe.loading.steps.fetchingEmbeddings')}
            description={t('universe.loading.description')}
          />
        )}
        {error && (
          <UniverseState
            kind={error.kind}
            title={t(error.kind === 'empty' ? 'universe.empty.title' : 'universe.error.title')}
            description={t(error.kind === 'empty' ? 'universe.empty.body' : 'universe.error.body')}
            actionLabel={t('universe.retry')}
            onAction={handleRetry}
          />
        )}
        {!loading && !error && universeData && (
          <>
            {subTab === 'graph' && (
              <GraphView universeData={universeData} articleMap={articleMap} archivedIds={archivedIds} />
            )}
            {subTab === 'map' && (
              <Suspense fallback={(
                <UniverseState
                  kind="loading"
                  title={t('universe.loading.view')}
                  description={t('universe.loading.description')}
                />
              )}>
                <MapView universeData={universeData} articleMap={articleMap} />
              </Suspense>
            )}
            {subTab === 'topics' && (
              <Suspense fallback={(
                <UniverseState
                  kind="loading"
                  title={t('universe.loading.view')}
                  description={t('universe.loading.description')}
                />
              )}>
                <TopicsView universeData={universeData} articleMap={articleMap} />
              </Suspense>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UniverseView;
